import BaseView from './BaseView';
let view = null;

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

import FeaturesCanvas from '../../components/FeaturesCanvas';

class TemplateFeatures extends BaseView {
	constructor(container) {
		super(container);
	}

	init() {
		this.zIndex = 10;

		this.featureMedias = this.container.querySelectorAll('[data-feature-media]');
		this.featureItems = this.container.querySelectorAll('[data-feature-item]');
		this.featureLinks = this.container.querySelectorAll('[data-feature-link]');

		this.lockBody();

		const mm = gsap.matchMedia();
		this.mm = mm.add(
			{
				isMobile: `(max-width: 991px)`,
				isDesktop: `(min-width: 992px)`,
				reduceMotion: '(prefers-reduced-motion: reduce)',
			},
			(context) => {
				let { isMobile, isDesktop, reduceMotion } = context.conditions;

				if (isMobile) {
					this.initScrollTriggers();
				}
				if (isDesktop) {
					this.killScrollTriggers();
					this.initHovers();
				}
			}
		);

		this.featureItems.forEach((elm) => {
			elm.querySelector('a').addEventListener('focus', (e) => {
				this.activateItem(elm);
			});
			elm.querySelector('a').addEventListener('blur', (e) => {
				this.deactivateItem(elm);
			});
		});

		this.featuresCanvasElm = this.container.querySelector('#canvas');
		if (this.featuresCanvasElm) {
			this.featuresCanvas = new FeaturesCanvas(this.featuresCanvasElm);
		}

		this.lockContainer();

		this.createIntroTimeline();
	}

	createIntroTimeline() {
		if (this.isMobile) {
			this.unlockContainer();
		}

		this.introTimeline = gsap.timeline({
			paused: true,
		});

		this.introTimeline.to(this.featureItems, {
			autoAlpha: 1,

			duration: 0.8,
			delay: 0.8,
			ease: 'none',
		});

		this.introTimeline.to(this.featureItems, {
			autoAlpha: 1, // Extra delay
			duration: 0.4,
		});

		this.introTimeline.addLabel('fade');

		this.introTimeline.from(this.featureLinks, {
			x: 0,

			duration: 1.4,
			ease: 'expo.inOut',
			delay: 0,
			clearProps: 'x',
			onComplete: () => {
				this.featuresCanvas.positionBalls();
				this.unlockContainer();

				this.addDots();
				this.switcherTimeout = setTimeout(() => {
					document.dispatchEvent(new CustomEvent('show-switcher'));
				}, 1400);
			},
		});
	}

	initIntro() {
		this.introTimeline.play();
	}

	skipIntro() {
		this.introTimeline.seek('fade').play();
	}

	lockBody() {
		document.dispatchEvent(
			new CustomEvent('disable-scroll', {
				detail: {
					scrollingElm: this.container,
				},
			})
		);

		this.container.classList.add('ready-to-scroll');
	}

	lockContainer() {
		this.container.classList.remove('ready-to-scroll');
	}

	unlockContainer() {
		this.container.classList.add('ready-to-scroll');
	}

	addDots() {
		this.featureItems.forEach((item) => {
			item.querySelector('.c-feature-item__link').classList.remove('is-transitioning');
		});

		this.featuresCanvasElm = this.container.querySelector('#canvas');
		gsap.to(this.featuresCanvasElm, {
			autoAlpha: 0.3,
			duration: 1,
			ease: 'none',
		});
	}

	initHovers() {
		this.featureItems.forEach((elm) => {
			elm.addEventListener('mouseenter', () => {
				this.activateItem(elm);
				document.dispatchEvent(new CustomEvent('hide-gradient'));
			});
			elm.addEventListener('mouseleave', () => {
				this.deactivateItem(elm);
				document.dispatchEvent(new CustomEvent('show-gradient'));
			});
		});
	}

	initScrollTriggers() {
		this.scrollTrigger = this.featureItems.forEach((elm) => {
			ScrollTrigger.create({
				scroller: this.container,
				trigger: elm,
				start: 'top ' + window.innerHeight / 2,
				end: 'bottom ' + window.innerHeight / 2,
				onEnter: () => {
					this.activateItem(elm);
				},
				onEnterBack: () => {
					this.activateItem(elm);
				},
				onLeave: () => {
					this.deactivateItem(elm);
				},
				onLeaveBack: () => {
					this.deactivateItem(elm);
				},
			});
		});
	}

	killScrollTriggers() {
		if (this.scrollTrigger) {
			this.scrollTrigger.kill();
		}
		this.featureItems.forEach((elm) => {
			this.deactivateItem(elm);
		});
	}

	activateItem(elm) {
		elm.classList.add('is-active');
		const featureMedia = this.container.querySelector('[data-feature-media="' + elm.dataset.featureItem + '"');
		featureMedia.classList.add('is-active');

		this.zIndex = this.zIndex + 1;
		gsap.set(featureMedia, {
			zIndex: this.zIndex,
		});
		gsap.to(featureMedia, {
			opacity: 1,
			ease: 'none',
			duration: 0.5,
			overwrite: true,
		});
	}

	deactivateItem(elm) {
		elm.classList.remove('is-active');

		const featureMedia = this.container.querySelector('[data-feature-media="' + elm.dataset.featureItem + '"');
		gsap.to(featureMedia, {
			opacity: 0,
			ease: 'none',
			duration: 0.5,
			overwrite: true,
			onComplete: () => {
				featureMedia.classList.remove('is-active');
			},
		});
	}

	destroy() {
		this.container.classList.remove('ready-to-scroll');
		clearTimeout(this.switcherTimeout);
		document.dispatchEvent(new CustomEvent('show-gradient'));

		if (this.featuresCanvas) {
			this.featuresCanvas.destroy();
		}
	}
}

export default {
	namespace: 'template-features',
	beforeEnter: ({ current, next, trigger }) => {
		view = new TemplateFeatures(next.container);
		view.init();
		if (current.container == null) {
			view.initIntro();
		} else {
			view.skipIntro();
		}
	},
	afterLeave: ({ current, next, trigger }) => {
		view.destroy();
		view = null;
	},
};
